import { StackScreenProps } from '@react-navigation/stack'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { StyleSheet, View } from 'react-native'
import {
  Button,
  TextInput,
  Text,
  Card,
  Portal,
  Modal,
} from 'react-native-paper'
import Toast from 'react-native-root-toast'

import * as Sentry from '@sentry/react-native'
import { MessageType } from '../../../../utils/message-type'
import {
  UpdateClusterInput,
  useClusterByIdQuery,
  useDeleteClusterMutation,
  useUpdateClusterMutation,
  ClusterByIdQuery,
  useRemoveSelfFromClusterMutation,
} from '../../../../generated/graphql'
import { AppNavigatorParams } from '../../../../navigation/types'
import { useTheme } from '../../../../hooks/theme.hook'
import {
  Column,
  LabeledSwitch,
  MelddCard,
  Row,
  ScreenWrapper,
} from '../../../../@ui'
import { FilterChips, FilterOption } from '../../../../@ui/buttons/filter.chips'
import { ClusterMemberList } from './components/cluster-member.list'
import ClusterMemberSearch from './components/cluster-member.search'
import { TransferClusterOwnershipModal } from './components/transfer-cluster-ownership.modal'
import { TransferClusterToNodeModal } from './components/transfer-cluster-to-node.modal'

type TabValue = 'settings' | 'members'

// Settings Tab Component
interface ClusterSettingsTabProps {
  cluster: ClusterByIdQuery['clusterById']
  onCancel: () => void
  onTransferOwnership: () => void
  onLeaveCluster: () => void
  onTransferToNode: () => void
}

const ClusterSettingsTab = ({
  cluster,
  onCancel,
  onTransferOwnership,
  onLeaveCluster,
  onTransferToNode,
}: ClusterSettingsTabProps) => {
  const { t } = useTranslation('clusters')
  const theme = useTheme()

  // Original values
  const [originalValues, setOriginalValues] = useState({
    topic: cluster.topic || '',
    isPrivate: cluster.isPrivate || false,
    showModerators: cluster.showModerators || false,
    showPerspectiveOwners: cluster.showPerspectiveOwners || false,
  })

  // Component-specific state
  const [topic, setTopic] = useState<string>(cluster.topic || '')
  const [isPrivate, setIsPrivate] = useState<boolean>(
    cluster.isPrivate || false
  )
  const [showModerators, setShowModerators] = useState<boolean>(
    cluster.showModerators || false
  )
  const [showPerspectiveOwners, setShowPerspectiveOwners] = useState<boolean>(
    cluster.showPerspectiveOwners || false
  )

  // Check if form is dirty
  const isDirty = useMemo(() => {
    return (
      topic !== originalValues.topic ||
      isPrivate !== originalValues.isPrivate ||
      showModerators !== originalValues.showModerators ||
      showPerspectiveOwners !== originalValues.showPerspectiveOwners
    )
  }, [topic, isPrivate, showModerators, showPerspectiveOwners, originalValues])

  const [doUpdateCluster, { loading }] = useUpdateClusterMutation({
    refetchQueries: ['availableClusters', 'clusterById'],
  })
  const [doDeleteCluster, { loading: deleting }] = useDeleteClusterMutation({
    update: (cache) => {
      const field = `availableClusters(${JSON.stringify({
        nodeId: cluster.nodeId,
      })})`
      cache.modify({
        id: 'ROOT_QUERY',
        fields: {
          [field]: (existingTaskRefs, { readField }) => {
            return (existingTaskRefs as { id: string }[]).filter(
              (taskRef) => cluster.id !== readField('id', taskRef)
            )
          },
        },
      })
    },
  })

  const handleCancelChanges = () => {
    setTopic(originalValues.topic)
    setIsPrivate(originalValues.isPrivate)
    setShowModerators(originalValues.showModerators)
    setShowPerspectiveOwners(originalValues.showPerspectiveOwners)
  }

  const handleUpdateCluster = async () => {
    const input: UpdateClusterInput = {
      topic,
      isPrivate,
      showModerators,
      clusterId: cluster.id,
      showPerspectiveOwners,
    }
    try {
      await doUpdateCluster({
        variables: { input },
      })
      // Update original values after successful save
      setOriginalValues({
        topic,
        isPrivate,
        showModerators,
        showPerspectiveOwners,
      })
      Toast.show(t('Cluster updated'))
    } catch (e) {
      Toast.show((e as Error).message, MessageType.error)
      console.error(e)
      Sentry.captureException(e)
    }
  }

  const handleDeleteCluster = async () => {
    try {
      await doDeleteCluster({
        variables: { id: cluster.id },
      })
      Toast.show(t('Cluster deleted'))
      onCancel()
    } catch (e) {
      Toast.show((e as Error).message, MessageType.error)
      console.error(e)
      Sentry.captureException(e)
    }
  }

  const styles = useMemo(
    () =>
      StyleSheet.create({
        containerButtons: {
          marginVertical: 20,
          flexDirection: 'row',
          justifyContent: 'space-evenly',
        },
      }),
    []
  )

  return (
    <>
      <Card style={{ padding: theme.spacing(4) }}>
        <Column gap={theme.spacing(1)}>
          <TextInput
            label={t('Cluster Topic')}
            onChangeText={(text) => setTopic(text)}
            value={topic}
            clearButtonMode={'while-editing'}
            mode={'outlined'}
          />
          <LabeledSwitch
            style={{ alignSelf: 'flex-end' }}
            wrapperStyle={{
              alignSelf: 'flex-end',
              columnGap: theme.spacing(4),
            }}
            label={t('Is Private?')}
            value={isPrivate}
            onValueChange={setIsPrivate}
          />
          <LabeledSwitch
            style={{ alignSelf: 'flex-end' }}
            wrapperStyle={{
              alignSelf: 'flex-end',
              columnGap: theme.spacing(4),
            }}
            label={t('Show Moderators')}
            value={showModerators}
            onValueChange={setShowModerators}
          />
          <LabeledSwitch
            style={{ alignSelf: 'flex-end' }}
            wrapperStyle={{
              alignSelf: 'flex-end',
              columnGap: theme.spacing(4),
            }}
            label={t('Show Perspective Owners')}
            value={showPerspectiveOwners}
            onValueChange={setShowPerspectiveOwners}
          />

          {isDirty && (
            <View style={styles.containerButtons}>
              <Button onPress={handleCancelChanges} disabled={loading}>
                {t('Cancel')}
              </Button>
              <Button
                onPress={handleUpdateCluster}
                mode="contained"
                disabled={loading || !topic}
              >
                {t('Update')}
              </Button>
            </View>
          )}
        </Column>
      </Card>

      <Text variant="titleMedium">{t('danger-cluster-settings')}</Text>
      <Row
        alignItems="center"
        columnGap={theme.spacing(1)}
        wrap={true}
        style={{ marginTop: theme.spacing(1), overflow: 'scroll' }}
      >
        {cluster.isOwner ? (
          <>
            <Button
              onPress={onTransferOwnership}
              mode="outlined"
              icon="account-arrow-right"
            >
              {t('transfer-ownership')}
            </Button>

            <Button
              onPress={handleDeleteCluster}
              textColor={theme.colors.danger.main}
              mode="outlined"
              theme={{
                colors: {
                  primary: theme.colors.danger.main,
                  outline: theme.colors.danger.main,
                },
              }}
            >
              {t('Delete cluster')}
            </Button>
          </>
        ) : (
          <Button
            onPress={onLeaveCluster}
            textColor={theme.colors.danger.main}
            mode="outlined"
            icon="exit-to-app"
            theme={{
              colors: {
                primary: theme.colors.danger.main,
                outline: theme.colors.danger.main,
              },
            }}
          >
            {t('leave-cluster')}
          </Button>
        )}
        {cluster.isModerator && (
          <Button
            onPress={onTransferToNode}
            textColor={theme.colors.danger.main}
            mode="outlined"
            icon="swap-horizontal"
            theme={{
              colors: {
                primary: theme.colors.danger.main,
                outline: theme.colors.danger.main,
              },
            }}
          >
            {t('transfer-to-node')}
          </Button>
        )}
      </Row>
    </>
  )
}

// Members Tab Component
interface ClusterMembersTabProps {
  cluster: ClusterByIdQuery['clusterById']
}

const ClusterMembersTab = ({ cluster }: ClusterMembersTabProps) => {
  const [searchMode, setSearchMode] = useState<'filter' | 'search'>('filter')

  return (
    <>
      {searchMode === 'search' ? (
        <ClusterMemberSearch
          cluster={cluster}
          onUpdate={() => {}}
          onSwitchMode={() => setSearchMode('filter')}
        />
      ) : (
        <ClusterMemberList
          cluster={cluster}
          onUpdate={() => {}}
          isOwner={cluster.isOwner}
          onSwitchMode={() => setSearchMode('search')}
        />
      )}
    </>
  )
}

export function ClusterSettingsScreen({
  navigation,
  route,
}: StackScreenProps<AppNavigatorParams, 'ClusterSettings'>) {
  const { t } = useTranslation('clusters')
  const theme = useTheme()

  const { clusterId } = route.params
  const { data } = useClusterByIdQuery({ variables: { id: clusterId } })
  const cluster = data?.clusterById

  const [selectedTab, setSelectedTab] = useState<TabValue>('settings')
  const [isTransferModalVisible, setIsTransferModalVisible] = useState(false)
  const [isTransferNodeModalVisible, setIsTransferNodeModalVisible] =
    useState(false)
  const [showLeaveConfirmation, setShowLeaveConfirmation] = useState(false)

  const [removeSelfFromCluster, { loading: leavingCluster }] =
    useRemoveSelfFromClusterMutation({
      refetchQueries: ['availableClusters', 'clusterById'],
    })

  const tabs: FilterOption<TabValue>[] = [
    { value: 'settings', label: t('Settings') },
  ]
  if (cluster?.isOwner) {
    tabs.push({ value: 'members', label: t('Members') })
  }

  const handleTransferOwnership = () => {
    if (!cluster) {
      return
    }

    Toast.show(t('transfer-ownership-from-members-menu'), {
      position: Toast.positions.TOP,
    })
    setSelectedTab('members')

    // Public and private both do it from the member menu
    // setIsTransferModalVisible(true)
  }

  const handleLeaveCluster = async () => {
    setShowLeaveConfirmation(true)
  }

  const confirmLeaveCluster = async () => {
    if (!cluster) {
      return
    }

    try {
      await removeSelfFromCluster({
        variables: { clusterId: cluster.id },
      })
      Toast.show(t('left-cluster-successfully'))
      setShowLeaveConfirmation(false)
      navigation.navigate('ClusterCards', { clusterId: cluster.id })
    } catch (e) {
      Toast.show((e as Error).message, MessageType.error)
      console.error(e)
      Sentry.captureException(e)
      setShowLeaveConfirmation(false)
    }
  }

  const styles = useMemo(
    () =>
      StyleSheet.create({
        container: {
          flexDirection: 'column',
          padding: 20,
          alignItems: 'stretch',
          rowGap: theme.spacing(4),
        },
        tabsContainer: {
          marginBottom: theme.spacing(4),
          alignItems: 'center',
        },
      }),
    [theme]
  )

  if (!cluster) return null

  return (
    <ScreenWrapper contentContainerStyle={styles.container}>
      {/* Tabs */}
      <Row justifyContent="center" alignItems="center">
        <FilterChips<TabValue>
          options={tabs}
          selectedValue={selectedTab}
          onValueChange={setSelectedTab}
        />
      </Row>

      {/* Settings Tab */}
      {selectedTab === 'settings' && (
        <ClusterSettingsTab
          cluster={cluster}
          onCancel={() =>
            navigation.navigate('ClusterCards', { clusterId: cluster.id })
          }
          onTransferOwnership={handleTransferOwnership}
          onLeaveCluster={handleLeaveCluster}
          onTransferToNode={() => setIsTransferNodeModalVisible(true)}
        />
      )}

      {/* Members Tab */}
      {selectedTab === 'members' && <ClusterMembersTab cluster={cluster} />}

      {/* Transfer Ownership Modal */}
      <TransferClusterOwnershipModal
        visible={isTransferModalVisible}
        onDismiss={() => setIsTransferModalVisible(false)}
        cluster={cluster}
      />

      {/* Transfer to Node Modal */}
      <TransferClusterToNodeModal
        visible={isTransferNodeModalVisible}
        onDismiss={() => setIsTransferNodeModalVisible(false)}
        cluster={cluster}
      />

      {/* Leave Cluster Confirmation Modal */}
      <Portal>
        <Modal
          visible={showLeaveConfirmation}
          onDismiss={() => setShowLeaveConfirmation(false)}
          contentContainerStyle={[
            {
              backgroundColor: theme.colors.surface,
              padding: theme.spacing(4),
              margin: theme.spacing(4),
              borderRadius: theme.spacing(2),
            },
          ]}
        >
          <Text variant="titleLarge" style={{ marginBottom: theme.spacing(2) }}>
            {t('leave-cluster')}
          </Text>
          <Text variant="bodyMedium" style={{ marginBottom: theme.spacing(4) }}>
            {t('leave-cluster-confirm', { clusterTopic: cluster.topic })}
          </Text>
          <View
            style={{
              flexDirection: 'row',
              justifyContent: 'space-evenly',
            }}
          >
            <Button
              onPress={() => setShowLeaveConfirmation(false)}
              disabled={leavingCluster}
            >
              {t('cancel')}
            </Button>
            <Button
              mode="contained"
              onPress={confirmLeaveCluster}
              loading={leavingCluster}
              disabled={leavingCluster}
              buttonColor={theme.colors.error}
            >
              {t('leave')}
            </Button>
          </View>
        </Modal>
      </Portal>
    </ScreenWrapper>
  )
}

export default ClusterSettingsScreen
